const parseJsonValue = (value) => {
  if (typeof value !== "string") return value;

  const trimmedValue = value.trim();
  if (!trimmedValue) return null;
  if (trimmedValue.charAt(0) !== "[" && trimmedValue.charAt(0) !== "{") {
    return value;
  }

  try {
    return JSON.parse(trimmedValue);
  } catch (error) {
    return value;
  }
};

const hasValue = (value) =>
  value !== null && typeof value !== "undefined" && value !== "";

const isEnabled = (value) => value === true || value === 1 || value === "1";

const isPositiveNumber = (value) =>
  hasValue(value) && !Number.isNaN(Number(value)) && Number(value) > 0;

const formatCurrency = (value) => {
  if (!hasValue(value)) return null;
  const amount = Number(value);
  if (Number.isNaN(amount)) return String(value);
  return "Rs. " + amount.toLocaleString();
};

const createBenefit = (key, text) =>
  hasValue(text) ? { key, text: String(text) } : null;

const formatAllowance = (value) =>
  String(value).toLowerCase() === "unlimited" ? "Unlimited" : value;

const formatDeliveryName = (type) => {
  const name = String(type || "Delivery").replace(/_/g, " ");
  return name.charAt(0).toUpperCase() + name.slice(1);
};

const compactBenefits = (benefits) => benefits.filter(Boolean);

export const groupOrdersByMonth = (orders) => {
  const groups = new Map();

  (orders || []).forEach((order) => {
    const date = new Date(order.created_at);
    if (Number.isNaN(date.getTime())) return;
    const key =
      date.getFullYear() + "-" + String(date.getMonth() + 1).padStart(2, "0");
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        label: date.toLocaleDateString(undefined, {
          month: "long",
          year: "numeric",
        }),
        orders: [],
      });
    }
    groups.get(key).orders.push(order);
  });

  return Array.from(groups.values());
};

const parseDeliveryBenefits = (value) => {
  const deliveryOptions = parseJsonValue(value);
  if (!Array.isArray(deliveryOptions)) return [];

  return deliveryOptions.reduce((benefits, option) => {
    if (!option || option.type === "sum") return benefits;

    const deliveryName = formatDeliveryName(option.type);

    const hasFreeDelivery = isEnabled(option.free);
    const hasDeliveryDiscount = isPositiveNumber(option.discount);
    const hasDeliveryPrice = isPositiveNumber(option.base_price);

    if (hasFreeDelivery) {
      benefits.push(
        createBenefit(
          "delivery-" + option.type + "-free",
          "Free " + deliveryName + " Delivery",
        ),
      );
    }

    if (hasDeliveryDiscount) {
      benefits.push(
        createBenefit(
          "delivery-" + option.type + "-discount",
          option.discount + "% " + deliveryName + " Delivery",
        ),
      );
    }

    if (!hasFreeDelivery && !hasDeliveryDiscount && hasDeliveryPrice) {
      benefits.push(
        createBenefit(
          "delivery-" + option.type + "-price",
          deliveryName + " Delivery " + formatCurrency(option.base_price),
        ),
      );
    }

    return benefits;
  }, []);
};

const parseHealthBenefits = (value) => {
  const parsedValue = parseJsonValue(value);
  const health = Array.isArray(parsedValue) ? parsedValue[0] : parsedValue;
  if (!health || typeof health !== "object") return [];

  return compactBenefits([
    isEnabled(health.flashOneAvailable)
      ? createBenefit("health-flash-one", "Flash One")
      : null,
    isEnabled(health.periodTrackerAvailable)
      ? createBenefit("health-period-tracker", "Period Tracker")
      : null,
  ]);
};

const parseCollectionBenefit = (feature, label) => {
  const parsedValue = parseJsonValue(feature.feature_value);
  if (!Array.isArray(parsedValue) || !parsedValue.length) return [];

  return [
    createBenefit(feature.feature_title, parsedValue.length + " " + label),
  ];
};

const parseClaimBenefit = (feature) => {
  const parsedValue = parseJsonValue(feature.feature_value);
  const hasClaim = Array.isArray(parsedValue)
    ? parsedValue.some((claim) => claim && typeof claim === "object")
    : parsedValue && typeof parsedValue === "object";

  return hasClaim ? [createBenefit(feature.id, "Claims")] : [];
};

export const parsePackageFeature = (feature) => {
  if (!feature || feature.is_active === 0 || feature.is_active === "0")
    return [];

  const value = parseJsonValue(feature.feature_value);

  switch (feature.feature_title) {
    case "medicine_discount":
      return isPositiveNumber(value)
        ? [createBenefit(feature.id, value + "% Medicine")]
        : [];
    case "lab_discount":
      return isPositiveNumber(value)
        ? [createBenefit(feature.id, value + "% Lab")]
        : [];
    case "mbe_price":
      return isPositiveNumber(value)
        ? [createBenefit(feature.id, "MBE " + formatCurrency(value))]
        : [];
    case "no_of_calls":
      return String(value).toLowerCase() === "unlimited" ||
        isPositiveNumber(value)
        ? [
            createBenefit(
              feature.id,
              formatAllowance(value) + " Talk Now Calls",
            ),
          ]
        : [];
    case "no_of_talk_global_calls":
      return String(value).toLowerCase() === "unlimited" ||
        isPositiveNumber(value)
        ? [
            createBenefit(
              feature.id,
              formatAllowance(value) + " Talk Global Calls",
            ),
          ]
        : [];
    case "no_of_talk_later_calls":
      return String(value).toLowerCase() === "unlimited" ||
        isPositiveNumber(value)
        ? [
            createBenefit(
              feature.id,
              formatAllowance(value) + " Talk Later Calls",
            ),
          ]
        : [];
    case "no_of_calls_talk_later":
      return parseCollectionBenefit(feature, "Talk Later Call Options");
    case "partner_and_coupon_management":
      return parseCollectionBenefit(feature, "Partner Benefits and Coupons");
    case "claim":
      return parseClaimBenefit(feature);
    case "talkLaterDoctorWise":
      return parseCollectionBenefit(feature, "Talk Later Doctors");
    case "your_health":
      return parseHealthBenefits(feature.feature_value);
    case "delivery":
      return parseDeliveryBenefits(feature.feature_value);
    default:
      return [];
  }
};

export const getPackageBenefits = (features) =>
  (features || []).reduce(
    (benefits, feature) => benefits.concat(parsePackageFeature(feature)),
    [],
  );

export const maskNumber = (value, visibleDigits) => {
  const digits = String(value || "").replace(/\s/g, "");
  if (!digits) return "Not available";
  const count = visibleDigits || 4;
  const ending = digits.slice(-count);
  return "•••• " + ending;
};

export const sanitizeCards = (cards) =>
  (Array.isArray(cards) ? cards : []).map((card) => ({
    id: card.id,
    brand: card.brand || "Card",
    maskedNumber: maskNumber(card.card_no, 4),
    expiryMonth: card.expiry_month || "—",
    expiryYear: card.expiry_year || "—",
    isActive:
      card.is_active === true || card.is_active === 1 || card.is_active === "1",
  }));

export const getClaimTypeLabel = (value) => {
  switch (Number(value)) {
    case 1:
      return "Medicine";
    case 2:
      return "Lab";
    case 3:
      return "Consultation";
    default:
      return "Claim";
  }
};

export const getClaimStatusLabel = (value) => {
  switch (Number(value)) {
    case 1:
      return "Submitted";
    case 2:
      return "Reviewed";
    case 3:
      return "Rejected";
    case 4:
      return "Completed";
    case 5:
      return "Reset";
    default:
      return value || "—";
  }
};

export const getConsultationStatus = (consultation) =>
  consultation.call_ended_type ||
  consultation.payment_status ||
  (consultation.end_time ? "Completed" : "Pending");

export const getInitials = (name) => {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "?";
  return (parts[0].charAt(0) + (parts[1] ? parts[1].charAt(0) : ""))
    .toUpperCase()
    .slice(0, 2);
};

export const displayBalance = (balance) => {
  if (!balance || balance.unavailable) return "Temporarily unavailable";
  let value = balance.available;
  if (value === null || typeof value === "undefined") return "—";
  if (
    typeof value === "object" &&
    Object.prototype.hasOwnProperty.call(value, "remainingCredit")
  )
    value = value.remainingCredit;
  const number = Number(value);
  return value !== "" && !Number.isNaN(number)
    ? number.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : value;
};

export const displayMoney = (value) => {
  if (value === null || typeof value === "undefined" || value === "")
    return "—";
  const number = Number(value);
  return Number.isNaN(number)
    ? value
    : "Rs. " +
        number.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
};

export const addressText = (address) =>
  [
    address.address_line1,
    address.address_line2,
    address.city,
    address.state,
    address.postal_code,
  ]
    .filter(Boolean)
    .join(", ") || "Address details unavailable";
